import { useMemo, useState } from 'react';
import { Spinner, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { useListInternalLayerVersionsQuery } from 'hooks/internalLayerVersion/useListInternalLayerVersionsQuery';
import { useListInternalLayersQuery } from 'hooks/internalLayer/useListInternalLayersQuery';

import { useRejectRequest, useApproveRequest } from 'hooks/useApprovals';
import { ITEM_TYPES } from 'constants/itemType';
import { useUscgMapContext } from './UscgMapContext';
import { internalLayerVersionsKeys } from 'hooks/internalLayerVersion/queryKeys';

export function ReviewQueuePanel({ onBack }: { onBack: () => void }) {
  const groupGuid = useGroupGuid();
  const { incidentFilterId } = useUscgMapContext();
  const approveRequest = useApproveRequest(internalLayerVersionsKeys.all());
  const rejectRequest = useRejectRequest(internalLayerVersionsKeys.all());

  const { data: layersData } = useListInternalLayersQuery({
    filter: { groupGuid: groupGuid ?? '', incidentId: incidentFilterId },
  });

  const {
    data: versionsData,
    isLoading: versionsLoading,
    error: versionsError,
  } = useListInternalLayerVersionsQuery({
    filter: { status: 'PENDING' },
    page: 1,
    perPage: 100,
  });

  const layerNameById = useMemo(() => {
    const map: Record<string, string> = {};
    for (const layer of layersData?.data ?? []) {
      map[layer.id] = layer.name;
    }
    return map;
  }, [layersData?.data]);

  // Make sure we only show versions are associated with the current incident filter
  // Review queue is only available in Incident Workspace, so we always only show versions associated with the current incident filter
  const pendingVersions =
    versionsData?.data.filter((v) => v.internalLayerId in layerNameById) ?? [];

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    null
  );
  const [decision, setDecision] = useState<'approve' | 'deny' | null>(null);
  const [comments, setComments] = useState('');

  const selectedVersion = pendingVersions.find(
    (v) => v.id === selectedVersionId
  );

  const selectedLayerName =
    layerNameById[selectedVersion?.internalLayerId ?? ''];

  const handleBackClick = () => {
    if (selectedVersion) {
      setSelectedVersionId(null);
      setDecision(null);
      setComments('');
    } else {
      onBack();
    }
  };

  const handleSelectVersion = (versionId: string) => {
    setSelectedVersionId(versionId);
    setDecision(null);
    setComments('');
  };

  return (
    <div className="d-flex flex-column gap-3 p-2">
      <div className="d-flex align-items-center gap-2">
        <StylishNewButton
          className="button button--link text-white p-0"
          onClick={handleBackClick}
          aria-label={
            selectedVersion ? 'Back to review queue' : 'Back to layers'
          }
        >
          <SharedIcon iconName="arrow_back" classes="me-2" />
        </StylishNewButton>
        <h5 className="my-0 fw-semibold">
          {selectedVersion
            ? `Reviewing ${selectedLayerName} (draft)`
            : 'Review queue'}
        </h5>
      </div>

      {versionsLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      )}

      {versionsError && (
        <div className="alert alert-danger" role="alert">
          Error loading review queue:{' '}
          {versionsError instanceof Error
            ? versionsError.message
            : 'Unknown error'}
        </div>
      )}

      {!versionsLoading &&
        !versionsError &&
        !selectedVersion &&
        pendingVersions.length === 0 && (
          <p className="text-muted mb-0">No items pending review.</p>
        )}

      {!versionsLoading &&
        !versionsError &&
        !selectedVersion &&
        pendingVersions.length > 0 && (
          <ul className="list-unstyled mb-0 d-flex flex-column gap-2">
            {pendingVersions.map((version) => (
              <li
                key={version.id}
                className="d-flex align-items-center justify-content-between gap-3 p-3 rounded border border-neutral-700 bg-neutral-800 bg-neutral-700-hover"
                role="button"
                onClick={() => handleSelectVersion(String(version.id))}
              >
                <div className="flex-grow-1 min-w-0">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-truncate">
                      {layerNameById[version.internalLayerId] ?? '—'}
                    </span>
                    <span className="text-muted">-</span>
                    <span className="fw-semibold">
                      v{version.versionNumber}
                    </span>
                  </div>
                </div>
                <SharedIcon iconName="edit" size="sm" />
              </li>
            ))}
          </ul>
        )}

      {!versionsLoading && !versionsError && selectedVersion && (
        <form
          className="d-flex flex-column gap-3 mt-2"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!selectedVersion || !decision) return;

            if (decision === 'approve') {
              await approveRequest.mutateAsync({
                itemType: ITEM_TYPES.InternalLayerVersion,
                itemId: selectedVersion.itemId,
                groupId: groupGuid,
                comments,
              });
            } else {
              await rejectRequest.mutateAsync({
                itemType: ITEM_TYPES.InternalLayerVersion,
                itemId: selectedVersion.itemId,
                groupId: groupGuid,
                comments,
              });
            }

            setSelectedVersionId(null);
            setDecision(null);
            setComments('');
            onBack();
          }}
        >
          <div>
            <label className="form-label text-white d-block mb-2">
              Decision
            </label>
            <ToggleButtonGroup
              type="radio"
              name="review-decision"
              value={decision}
              onChange={(val: 'approve' | 'deny') => setDecision(val)}
            >
              <ToggleButton
                id="decision-approve"
                value="approve"
                variant={
                  decision === 'approve' ? 'primary' : 'outline-secondary'
                }
              >
                Approve
              </ToggleButton>
              <ToggleButton
                id="decision-deny"
                value="deny"
                variant={decision === 'deny' ? 'primary' : 'outline-secondary'}
              >
                Deny
              </ToggleButton>
            </ToggleButtonGroup>
          </div>

          <div>
            <label
              htmlFor="review-comments"
              className="form-label text-white d-block"
            >
              Comments to Submitter
            </label>
            <textarea
              id="review-comments"
              className="form-control bg-neutral-800 border-neutral-700 text-white"
              rows={4}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any comments for the submitter..."
            />
          </div>

          <div>
            <StylishNewButton
              type="submit"
              className="button--primary w-100"
              disabled={!decision}
            >
              Submit Review
            </StylishNewButton>
          </div>
        </form>
      )}
    </div>
  );
}
